// SessionDrawer.jsx — Item G (agent-capabilities): the card-detail slide-over, redesigned to the
// canonical task-panel design (data-design="spotlight", data-density="compact").
//
// Right-side drawer opened by clicking a board card. Layout (plan-review decision 2026-07-02):
//   cover header (channel gradient, status pill)
//   → P4 approval actions WHEN the card has a pending bound approval (visually PRIMARY)
//   → "Ask Vesta about this task" composer (TOP slot when there is no pending action)
//   → Task details: Description (item.body via AssistantMarkdown) + Properties grid
//   → session summary / artifacts (ReportArtifact) / social calendar link.
//
// The raw AG-UI step log (the duration header + RUN_STARTED / TOOL_CALL debug rows) is KILLED —
// replay.steps is still produced by sessionReplay.buildReplay (isEmpty + tests depend on it) but
// never rendered.
//
// PRESERVED CONTRACTS (load-bearing — do not drop):
//   - P4 approval buttons (.pgs-approve/.pgs-decline) + the server-enforced double opt-in resubmit.
//   - onAsk(text, item.task_ref) — the board→chat handoff (m3-15), byte-for-byte.
//   - item.artifact + replay.artifacts → ReportArtifact (the only real deliverable pipeline).
//   - Social "View Content Calendar" real-navigation link.
//   - onResolved not passed at the board mount — board refresh rides the Task broadcast (intended).

import React, { useEffect, useState } from "react"
import { fetchData, postData } from "../utils/api"
import { buildReplay } from "./sessionReplay.js"
import { buildBoardSubmitBody } from "../pages/interactionComponent.js"
import ShellIcon from "./icons.jsx"
import VMark from "./VMark.jsx"
import ReportArtifact from "./ReportArtifact.jsx"
import AssistantMarkdown from "./AssistantMarkdown.jsx"
// P2 — the pure errored/retry view-model for an approve-then-apply FAILURE, shared VERBATIM with the
// chat surface (AgentStudioChat's ChatInteractionCard) so the two cannot drift.
import { applyFailureFrom, applyFailureFromError, approveLabel, failureView } from "./approvalFailure.js"

// E3 (agent-capabilities): the empirically-confirmed Social Media AI content calendar route —
// EventCalendarCampaignPage, the page that actually renders company_post drafts bucketed by
// scheduled_at (it fetches company_posts; SocialMediaDashboardPage does NOT). The new-tab "View
// Content Calendar" button on a social card opens THIS real page. Must equal Epic C's
// CONFIRMED_CALENDAR_PATH — same value, they must not drift.
const SOCIAL_CALENDAR_PATH = "/marketing/social_media_ai/campaign"

// Channel gradient (design SPECS §6 CH_GRAD, 145°) — cover header accent ONLY, never a button fill.
const CH_GRAD = {
  google: "linear-gradient(145deg,#4285F4 0%,#34A853 100%)",
  meta: "linear-gradient(145deg,#6A3AFB 0%,#822395 100%)",
  social: "linear-gradient(145deg,#34C759 0%,#193761 100%)",
  email: "linear-gradient(145deg,#FF7322 0%,#8E3215 100%)",
  calendars: "linear-gradient(145deg,#A500C6 0%,#2A0A52 100%)",
  press: "linear-gradient(145deg,#FF9500 0%,#8a6d00 100%)",
  vesta: "linear-gradient(145deg,#7C2EE6 0%,#2E58E2 55%,#10C8BF 100%)",
}

// Status tone (design SPECS §2): done|now|need|opp — client-derived from serializer fields.
const STATUS_LABEL = { done: "Completed", now: "In progress", need: "Needs you", opp: "Opportunity" }

// Actor display names (mirrors ProjectBoardPage's actorMap — serializer emits kind keys).
const ACTOR_NAMES = { agent: "Vesta Agent", team: "Vesta Team", auto: "Auto-sync", you: "You" }

export default function SessionDrawer({ item, onClose, onAsk, onResolved }) {
  const [replay, setReplay] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [ask, setAsk] = useState("")
  // P4 — board-surface approval state (only used when item.capability is present).
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  // P2 — the approve-then-apply FAILURE, fed from TWO doors carrying the SAME server stamp:
  //   * DURABLE  — item.apply_failure (Task.payload, whitelisted through by BoardController), so
  //                reopening the drawer shows the card exactly as it was left
  //   * IMMEDIATE — the 403 body of the approve we just POSTed. This one is load-bearing: on the
  //                 failure path nothing resolves, so the drawer stays OPEN and would otherwise show
  //                 an unchanged "Approve" — the exact hang this fixes.
  // Local state wins over the durable seed (it is the newest truth after a retry).
  const [liveFailure, setLiveFailure] = useState(null)

  const sessionId = item && (item.session_id || item.sessionId || item.session_ref)

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  useEffect(() => {
    if (!sessionId) { setReplay(null); return }
    let cancelled = false
    setLoading(true); setError(null)
    fetchData(`api/agent/session_replay/${sessionId}`)
      .then((data) => { if (!cancelled) setReplay(buildReplay(data)) })
      .catch((err) => { if (!cancelled) setError(err.message || "Failed to load session") })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [sessionId])

  if (!item) return null

  const submitAsk = () => {
    const text = ask.trim()
    if (!text) return
    if (typeof onAsk === "function") onAsk(text, item.task_ref || null)
    setAsk("")
    onClose()
  }

  // P4 — BOARD-SURFACE APPROVAL. Rendered ONLY when the board serializer minted a per-viewer
  // capability for THIS card (item.capability — a still-`blocked` money/send card, bound viewer). For
  // every other card (reports / done / non-bound) item.capability is absent → the drawer stays
  // read-only. Approving here resolves the SAME MoneyAction/Session/Task as the chat surface (one
  // interactions#submit endpoint, one MoneyAction#with_lock apply); the bound Task's
  // after_update_commit broadcast then refetches every mounted board.
  const capability = item && item.capability
  const canApprove = Boolean(capability && capability.token && capability.interaction_id)
  const submitPath = `api/agent/sessions/${item && (item.session_id || item.session_ref)}/interactions/submit`

  const resolveAndClose = () => {
    if (typeof onResolved === "function") onResolved(item)
    onClose()
  }

  const errMessage = (err) =>
    (err && err.response && err.response.data && (err.response.data.reason || err.response.data.error)) ||
    (err && err.message) ||
    "Could not submit — please try again."

  // P2 — an APPLY FAILURE is not an ordinary submit error. When the 403 carries the structured
  // `apply_failure` stamp we flip the card into the errored/retry state (plain-language copy +
  // attempt count) instead of dumping "apply_refused: funnel_failed_at_adset: …" into the note line.
  const reportError = (err) => {
    const failure = applyFailureFromError(err)
    if (failure) { setLiveFailure(failure); setSubmitError(null); return }
    setSubmitError(errMessage(err))
  }

  // The durable seed loses to anything this drawer has learned since it opened.
  const applyFailure = liveFailure || applyFailureFrom(item && item.apply_failure)
  const failure = failureView(applyFailure, { submitting })
  // "Retry" after a failure, "Approve" otherwise — same purple .pgs-approve either way (a primary
  // action is NEVER a gray/native button in this repo).
  const approveButtonLabel = approveLabel(applyFailure, { submitting })

  // Approve = the SERVER-ENFORCED double opt-in. The first POST carries only the approval capability;
  // a money card 403s `confirm_required` with a fresh short-TTL confirm token → we AUTO-RESUBMIT once
  // with it (the second human click is the drawer button itself). A plain (non-money) approval never
  // 403s and resolves on the first POST.
  const handleApprove = async () => {
    const body = buildBoardSubmitBody(capability, { approved: true })
    if (!body || submitting) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await postData(submitPath, body)
      resolveAndClose()
    } catch (err) {
      const data = err && err.response && err.response.data
      const confirm = data && data.error === "confirm_required" && data.confirm_capability && data.confirm_capability.token
      if (confirm) {
        try {
          await postData(submitPath, { ...body, confirm_capability: data.confirm_capability.token })
          resolveAndClose()
          return
        } catch (err2) {
          // The double opt-in means the EXECUTE leg runs on this second POST — so this is the catch
          // an apply failure actually lands in.
          reportError(err2)
        }
      } else {
        reportError(err)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDecline = async () => {
    const body = buildBoardSubmitBody(capability, { reason: "declined by user" })
    if (!body || submitting) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await postData(submitPath, { ...body, decision: "reject" })
      resolveAndClose()
    } catch (err) {
      reportError(err)
    } finally {
      setSubmitting(false)
    }
  }

  // Derive channel from the item if available (set by board serializer)
  const itemChannel = item.channel || "vesta"
  const CH_COLORS = {
    calendars: "#A500C6", meta: "#6A3AFB", social: "#1FA84F",
    email: "#FF7322", google: "#4285F4", press: "#E08600", vesta: "#5349E5",
  }
  const CH_NAMES = {
    calendars: "Event Calendars", meta: "Meta Ads", social: "Social Media",
    email: "Email Marketing", google: "Google Ads", press: "Press Releases", vesta: "Vesta",
  }
  const channelColor = CH_COLORS[itemChannel] || "#5349E5"
  const channelName  = CH_NAMES[itemChannel]  || itemChannel

  // Status pill tone — client-derived (design SPECS §2).
  const statusTone = item.status === "done" ? "done"
    : item.status === "needs_you" ? (item.is_opp ? "opp" : "need")
    : "now"

  const actorName = ACTOR_NAMES[item.actor] || item.actor || "Vesta Agent"
  const whenText = item.time || (item.created_at ? new Date(item.created_at).toLocaleString() : "—")

  // Description text: the card's full body (the board card shows a 4-line preview — Item F),
  // falling back to the session summary. Rendered through AssistantMarkdown (safe, escaped).
  const descText = item.body || (replay && replay.session && replay.session.summary) || ""

  return (
    <div className="pgs-drawer-scrim" onClick={onClose}>
      <div className="pgs-drawer" role="dialog" aria-label={item.title} onClick={(e) => e.stopPropagation()}>
        {/* Cover header — channel gradient 145°, white text, status pill (design SPECS §6). */}
        <div className="pgs-cover" style={{ background: CH_GRAD[itemChannel] || CH_GRAD.vesta }}>
          <span className={`pgs-status is-${statusTone}`}>{STATUS_LABEL[statusTone]}</span>
          <div className="pgs-cover__title">{item.title.replace(/^\[DEMO\]\s*/i, "")}</div>
          <div className="pgs-cover__meta">
            <span>{actorName}</span>
            <span>{whenText}</span>
            {item.task_ref && <span>{item.task_ref.id}</span>}
          </div>
          <button className="pgs-drawer__close pgs-cover__close" onClick={onClose} aria-label="Close">
            <ShellIcon name="x" size={16} />
          </button>
        </div>

        <div className="pgs-drawer__body">
          {/* P4 approval — PRIMARY slot. When the card has a pending bound approval this block
              renders ABOVE the composer (plan-review decision); when it doesn't, nothing renders
              here and the composer takes the top slot. Handlers unchanged (real money/send path). */}
          {canApprove && (
            <div className={failure ? "pgs-dactions pgs-dactions--failed" : "pgs-dactions"}>
              {/* P2 — the errored state. The card is STILL actionable on purpose (nothing was
                  consumed server-side and the retry is deliberate), so this explains what failed and
                  relabels the primary button rather than disabling it. */}
              {failure && (
                <div className="pgs-drawer__note pgs-drawer__note--err" role="status" data-testid="pgs-apply-failure">
                  <strong>This didn’t go through{failure.attempt ? ` — ${failure.attempt}` : ""}</strong>
                  <span className="pgs-drawer__note-body">{failure.message}</span>
                </div>
              )}
              {submitError && (
                <div className="pgs-drawer__note pgs-drawer__note--err">{submitError}</div>
              )}
              <button
                className="pgs-approve"
                onClick={handleApprove}
                disabled={submitting}
                aria-label={failure ? "Retry" : "Approve"}
              >
                {approveButtonLabel}
              </button>
              <button
                className="pgs-decline"
                onClick={handleDecline}
                disabled={submitting}
                aria-label="Decline"
              >
                Decline
              </button>
            </div>
          )}

          {/* Ask Vesta about this — the board→chat handoff (m3-15), moved into the body's top
              region (directly below the pending action when one exists, else the top slot).
              Vesta chat classes, not design tp-* tokens (RECON §5: chat UI is not design-governed). */}
          <div className="pgs-ask-top">
            <div className="pgs-ask-cap"><VMark size={18} radius={6} /> Ask Vesta about this task</div>
            <div className="pgs-ask-row">
              <input
                className="pgs-ask"
                placeholder="Ask Vesta about this…"
                value={ask}
                onChange={(e) => setAsk(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitAsk() } }}
                aria-label="Ask Vesta about this task"
              />
              <button className="pgs-ask-send" onClick={submitAsk} aria-label="Send" disabled={!ask.trim()}>
                <ShellIcon name="arrow-up" size={15} />
              </button>
            </div>
            <div className="pgs-ask-hint">Replies open in your agent chat on the left.</div>
          </div>

          {/* Task details — Description (safe markdown) + Properties grid (design SPECS §4/§7). */}
          <div className="pgs-details">
            {descText && (
              <div className="pgs-block">
                <div className="pgs-lbl">Description</div>
                <div className="pgs-desc"><AssistantMarkdown text={descText} /></div>
              </div>
            )}
            <div className="pgs-props">
              <div className="pgs-prop"><span className="k">Done by</span><span className="v">{actorName}</span></div>
              <div className="pgs-prop"><span className="k">Channel</span><span className="v"><i style={{ background: channelColor }} />{channelName}</span></div>
              <div className="pgs-prop"><span className="k">When</span><span className="v">{whenText}</span></div>
              <div className="pgs-prop"><span className="k">Task ID</span><span className="v mono">{item.task_ref ? item.task_ref.id : "—"}</span></div>
            </div>
          </div>

          {sessionId && (
            <>
              {loading && <div className="pgs-drawer__note">Loading session…</div>}
              {error && <div className="pgs-drawer__note pgs-drawer__note--err">{error}</div>}
              {/* Summary bubble — only when it isn't already the Description text (G1 dedup guard).
                  The raw step log that used to follow it is KILLED (Item G): replay.steps stays
                  populated in the view-model but is never rendered. */}
              {replay && replay.session && replay.session.summary && replay.session.summary !== descText && (
                <div className="pgs-bubble">{replay.session.summary}</div>
              )}
              {replay && replay.isEmpty && !loading && (
                <div className="pgs-drawer__note">No steps recorded for this session yet.</div>
              )}
            </>
          )}

          {/* S6 (Move B): a finished-work artifact carried ON the card itself (Session-independent) —
              e.g. job 10's press-release draft on a needs_you card. The board serializer emits it as
              `item.artifact`; we render it as a ReportArtifact REGARDLESS of whether a live Session
              exists, so the reviewer reads the draft + outlet links straight from the card. */}
          {item.artifact && (
            <div className="pgs-drawer__artifacts">
              <ReportArtifact render={{ payload: item.artifact }} />
            </div>
          )}

          {/* S5: report + approval artifacts hoisted out of the raw step timeline into
              replay.artifacts (sessionReplay buildReplay) and rendered as ReportArtifact cards —
              the drawer's deliverable slot (v1 = ReportArtifact only, per Billy's Q2 decision). */}
          {replay && replay.artifacts && replay.artifacts.length > 0 && (
            <div className="pgs-drawer__artifacts">
              {replay.artifacts.map((a) => <ReportArtifact key={a.seq} render={a.render} />)}
            </div>
          )}

          {/* E3: purple new-tab link to the REAL Social Media AI content calendar (the page that
              renders company_post drafts by scheduled_at). Shown for social-channel cards — this is
              Epic C's "give them a styled button to the content calendar" deliverable. It actually
              navigates (target=_blank), never a confirm-and-do-nothing. */}
          {itemChannel === "social" && (
            <a
              className="pgs-calbtn"
              href={SOCIAL_CALENDAR_PATH}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ShellIcon name="calendar" size={14} />
              View Content Calendar
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
